import type { Bonus, FaqItem, MethodStep, PreviewSlot, Testimonial, Volume } from '../types/content'

/** Replace with your real checkout link. */
export const checkoutUrl = '#checkout'
export const offerAnchor = '#offer'

export const images = {
  bundle: 'https://cdn.magicpatterns.com/uploads/eSoUYLJBZHKLoGnqgwAtZe/Mockup_de_los_materiales.png',
  mainCover: 'https://cdn.magicpatterns.com/uploads/fpGL5642FJ4ovGaHH9wFtx/PRINCIPAL.png',
}

export const childQuestions = [
  'Mom… what if something bad happens?',
  'Are you sure you’ll come back?',
  'What if I can’t do it?',
  'I don’t want to go.',
]

export const parentReplies = ['You’re okay.', 'Nothing bad is going to happen.', 'I promise.', 'Don’t worry.']

export const bravePathSteps = ['Worry', 'Recognize', 'Respond', 'Practice', 'Brave Step']

export const methodSteps: MethodStep[] = [
  { name: 'Recognize', text: 'See what may be happening beneath the behavior.', accent: 'sage' },
  { name: 'Respond', text: 'Know what to say and what may help in that moment.', accent: 'dusty' },
  { name: 'Practice', text: 'Turn coping into small, manageable brave steps.', accent: 'terra' },
]

/** Used by the Look Inside section (currently hidden). Paste a full image URL into each src when interior pages are ready. */
export const previewSlots: PreviewSlot[] = [
  { label: 'Scripts', src: '' },
  { label: 'Calming Tools', src: '' },
  { label: 'Coping Cards', src: '' },
  { label: 'Brave Steps', src: '' },
  { label: 'Parent Cheat Sheets', src: '' },
  { label: 'Activities', src: '' },
]

export const situations = [
  { id: 'dropoff', label: 'School Drop-Offs' },
  { id: 'bedtime', label: 'Bedtime Worries' },
  { id: 'whatif', label: '“What If?” Questions' },
  { id: 'separation', label: 'Separation' },
  { id: 'new', label: 'Trying Something New' },
  { id: 'feelings', label: 'Big Feelings' },
]

export const volumes: Volume[] = [
  {
    number: 1,
    title: 'When Anxiety Doesn’t Look Like Anxiety',
    description: 'Recognize worry hiding beneath everyday behavior.',
    cover: 'https://cdn.magicpatterns.com/uploads/cPVmRdLkmTTYsVTtJJFsQg/VOLUMEN_1.png',
    accent: 'dusty',
  },
  {
    number: 2,
    title: 'What to Say When Worry Takes Over',
    description: 'Know what to say when your child feels overwhelmed by worry.',
    cover: 'https://cdn.magicpatterns.com/uploads/fmdQxZMr1ogTnMkE6gKzcN/volumen_2.png',
    accent: 'sage',
  },
  {
    number: 3,
    title: 'Calm the Alarm',
    description: 'Support your child in calming their body and internal alarm.',
    cover: 'https://cdn.magicpatterns.com/uploads/fT66DShWdDQvZPxnvhUpem/volumen_3.png',
    accent: 'dusty',
  },
  {
    number: 4,
    title: 'Brave Goodbyes',
    description: 'Navigate separation, school drop-offs and difficult goodbyes.',
    cover: 'https://cdn.magicpatterns.com/uploads/gVPApmZ5SE21JvGe8BrEtb/voluem_4.png',
    accent: 'terra',
  },
  {
    number: 5,
    title: 'From “What If?” to “I Can”',
    description: 'Turn worry loops into coping and manageable brave action.',
    cover: 'https://cdn.magicpatterns.com/uploads/s5sQYtaVruDzZ3nuaijyMo/volumen_5.png',
    accent: 'gold',
  },
  {
    number: 6,
    title: 'Confidence in the Real World',
    description: 'Practice brave skills in everyday situations.',
    cover: 'https://cdn.magicpatterns.com/uploads/rjnMdSZUx6iMj2n6xpqGZo/volumen_6.png',
    accent: 'terra',
  },
]

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'The “What Do I Say?” Script Book',
    description: 'Ready-to-use words for the moments you’re not sure what to say.',
    cover: 'https://cdn.magicpatterns.com/uploads/s7EHWfdRQcAYGux7NG4vmr/bonus_1.png',
    audience: 'For you',
    accent: 'sage',
  },
  {
    number: 2,
    title: 'The Calm & Coping Cards',
    description: 'Visual tools your child can choose and practice.',
    cover: 'https://cdn.magicpatterns.com/uploads/6XfX1NWw6LHHCeLgUqMuM8/bonus_2.png',
    audience: 'For your child',
    accent: 'dusty',
  },
  {
    number: 3,
    title: 'My Brave Steps Kit',
    description: 'Break intimidating situations into smaller, manageable steps.',
    cover: 'https://cdn.magicpatterns.com/uploads/tDzmY24B5kmbqyoGVVBT5Z/bonus_3.png',
    audience: 'For you both',
    accent: 'terra',
  },
  {
    number: 4,
    title: 'I Can Handle This',
    description: 'Build the belief: “I can handle hard feelings.”',
    cover: 'https://cdn.magicpatterns.com/uploads/tzTjeS9gTcSCbehrGSuHES/bonus_4.png',
    audience: 'For your child',
    accent: 'gold',
  },
  {
    number: 5,
    title: 'The Parent Emergency Cheat Sheets',
    description: 'Quick-reference support when worry is already happening.',
    cover: 'https://cdn.magicpatterns.com/uploads/sLq5tFdqnGkVP9PWdA8zCw/bonus_5.png',
    audience: 'For you',
    accent: 'sage',
  },
]

/** Illustrative sample testimonials for the prototype — replace with real feedback before public launch. */
export const testimonials: Testimonial[] = [
  {
    headline: 'I finally knew what to say.',
    body: 'My daughter would ask the same worried questions over and over, and I kept repeating ‘You’re okay’ without really helping. The scripts gave me different words to use. For the first time, I didn’t feel completely stuck.',
  },
  {
    headline: 'School drop-offs became easier to navigate.',
    body: 'My son used to cling to me and beg me not to leave. Having simple steps to follow made mornings feel less overwhelming. We’re still working on it, but he’s starting to take those little brave steps.',
  },
  {
    headline: 'I stopped feeling like I had to fix every worry.',
    body: 'I used to feel responsible for convincing my child that everything would be okay. Now I focus on helping him handle the feeling instead of making every fear disappear. That shift alone made anxious moments feel lighter.',
  },
]

export const offerIncludes = [
  '6 Practical Volumes',
  '5 Ready-to-Use Bonuses',
  'Printable Resources',
  'Instant Digital Access',
  'Delivered Directly to Your Email',
  'Free Lifetime Updates',
  '7-Day Guarantee',
]

export const faqs: FaqItem[] = [
  { question: 'Is this a physical product?', answer: 'No. The Worry-to-Brave Toolkit is a 100% digital product.' },
  {
    question: 'How will I receive the toolkit?',
    answer: 'After your purchase, your access will be sent directly to the email address you used during checkout.',
  },
  {
    question: 'What ages is this designed for?',
    answer:
      'The toolkit is designed for children ages [add age range]. Every child is different, so you can choose and adapt the resources that feel like the best fit for your family.',
  },
  {
    question: 'Does my child need an anxiety diagnosis?',
    answer:
      'No. The toolkit is designed around common childhood worries, fears, anxious moments and coping skills. A diagnosis is not required to use the resources.',
  },
  {
    question: 'Is this therapy?',
    answer:
      'No. This is an educational parenting resource and is not a substitute for individualized medical, psychological or mental-health care.',
  },
  {
    question: 'Do I need to print everything?',
    answer: 'No. Parents can choose the materials that are most useful for a particular situation.',
  },
  {
    question: 'Will I receive future updates?',
    answer: 'Yes. Future updates to the toolkit are included at no additional cost.',
  },
]
